<script setup lang="ts">
import { ApiClientError } from "@shopware/api-client";
import type { ApiError } from "@shopware/api-client";
import { useTemplateRef } from "vue";
import { useVuelidate } from "@vuelidate/core";
import { email, required } from "@vuelidate/validators";

const emits = defineEmits<{
  (e: "success"): void;
  (e: "close"): void;
}>();
const router = useRouter();
const { isLoggedIn, login } = useUser();
const { t } = useI18n();
const { refreshSessionContext } = useSessionContext();
const { mergeWishlistProducts } = useWishlist();
const { pushSuccess } = useNotifications();
const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);
const loginErrors = ref<string[]>([]);
const loading = ref<boolean>();

const goToRegister = () => {
  emits("close");
  router.push(formatLink("/register"));
};

const route = useRoute()

const { resolveApiErrors } = useApiErrorsResolver("account_login");

const invokeLogin = async (): Promise<void> => {
  $v.value.$touch();
  const valid = await $v.value.$validate();
  console.log(valid);
  loginErrors.value = [];
  try {
    loading.value = true;
    // TODO: remove this line once the https://github.com/shopware/frontends/issues/112 issue is fixed
    await refreshSessionContext();
    await login(state);
    emits("success");
    pushSuccess(t("account.messages.loggedInSuccess"));
    emits("close");
    mergeWishlistProducts();
    if (!route.path.includes("/checkout")) {
      router.push(formatLink("/account"));
    }
  } catch (error) {
    if (error instanceof ApiClientError) {
      loginErrors.value = resolveApiErrors(error.details.errors as ApiError[]);
    }
  } finally {
    loading.value = false;
  }
};

const emailInputElement = useTemplateRef("emailInputElement");
useFocus(emailInputElement, { initialValue: true });

const rules = computed(() => ({
  username: {
    required,
    email,
  },
  password: {
    required,
  },
  remember: {},
}));

const state = reactive({
  username: "",
  password: "",
  remember: true,
});

const $v = useVuelidate(rules, state);
</script>
<template>
  <div id="modal-headline" role="form" title="Login form" aria-label="Login form"
    class="min-h-full flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
    <div v-if="!isLoggedIn" class="max-w-md w-full space-y-8">
      <div>
        <img class="mx-auto h-12 w-auto" src="assets/images/logo.svg" alt="logo of the shop" />
        <h2 class="mt-6 text-center text-3xl font-extrabold text-secondary-900">
          {{ $t("account.signInLabel") }}
        </h2>
      </div>
      <form class="mt-8 space-y-6" @submit.prevent="invokeLogin">
        <LoadingOverlay :show="loading" />
        <input v-model="state.remember" type="hidden" name="remember" data-testid="login-remember-input" />
        <div class="rounded-md -space-y-px">
          <div class="relative mb-6">
            <input id="email-address" ref="emailInputElement" v-model="state.username" name="email" type="email"
              autocomplete="email" required
              class="input-field input-sty peer placeholder-transparent focus:border-2 focus:border-dark-bg"
              :placeholder="$t('form.email')" data-testid="login-email-input" :class="{
                'border-red-600 focus:border-red-600 border-2':
                  $v.username?.$error,
              }" @input="$v.username.$touch()" />
            <label for="email-address"
              class="input-label top-3 label-sty peer-focus:transform peer-focus:-translate-y-5 peer-focus:text-xs peer-focus:text-dark-bg peer-focus:px-2 pointer-events-none"
              :class="{
                'transform -translate-y-5 text-xs px-2': state.username,
                'text-red-500': $v.username?.$error,
                'text-dark-bg': !$v.username?.$error,
              }">
              {{ $t("form.email") }}
            </label>

            <span v-if="$v.username?.$error" class="text-red-500 text-xs">
              {{ $v.username?.$errors[0]?.$message }}
            </span>
          </div>
          <div class="relative">
            <input id="password" v-model="state.password" name="password" type="password"
              autocomplete="current-password" required
              class="input-field input-sty peer placeholder-transparent focus:border-2 focus:border-dark-bg"
              :placeholder="$t('form.password')" data-testid="login-password-input" :class="{
                'border-red-600 focus:border-red-600 border-2':
                  $v.password?.$error,
              }" @input="$v.password.$touch()" />
            <label for="password"
              class="input-label top-3 label-sty peer-focus:transform peer-focus:-translate-y-5 peer-focus:text-xs peer-focus:text-dark-bg peer-focus:px-2 pointer-events-none"
              :class="{
                'transform -translate-y-5 text-xs px-2': state.password,
                'text-red-500': $v.password?.$error,
                'text-dark-bg': !$v.password?.$error,
              }">
              {{ $t("form.password") }}
            </label>

            <span v-if="$v.password?.$error" class="text-red-500 text-xs">
              {{ $v.password?.$errors[0]?.$message }}
            </span>
          </div>
        </div>

        <slot :data="state" />

        <slot name="error">
          <div v-if="loginErrors.length" class="flex items-center justify-between" data-testid="login-errors-container">
            <div class="flex items-center w-full">
              <div
                class="login-errors bg-red-300 text-red-800 text-sm text-left px-4 py-2 w-full border-secondary-300 rounded">
                {{ loginErrors[0] }}
              </div>
            </div>
          </div>
        </slot>

        <div>
          <button class="block btn-default-px-9 w-full btn-default btn-black-bg" type="submit"
            data-testid="login-submit-button">
            <span class="absolute left-0 inset-y-0 flex items-center pl-3">
              <div class="w-5 h-5 i-carbon-locked" />
            </span>
            {{ $t("account.signIn") }}
          </button>

          <slot name="action">
            <div class="w-full mt-4 [.login-wrap-box_&]:hidden" @click="$emit('close')">
              <button type="button"
                class="btn-default btn-transprent-bg w-full btn-w-icon justify-center mt-3 bg-transparent"
                data-testid="login-sign-up-link" @click="goToRegister()">
                {{ $t("account.signUp") }}
              </button>
            </div>
          </slot>
        </div>
      </form>
    </div>
  </div>
</template>
